import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../../shared/constants/static-data.constants';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { PageConfig } from '../../../../shared/authorization/auth.config';
import { Router } from '@angular/router';
import { ClaimsService } from '../../../../services/claims.service';
import { SectionsService } from '../../../../services/sections.service';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { AuthorizationService } from '../../../../shared/authorization/authorization.service';
import { SharedDataService } from '../../../../shared/services/shared-data-service';
import { BillingService } from '../../../../services/billing.service';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import { BillingFilterComponent } from '../../billing-filter/billing-filter.component';
import { ListService } from '../../../../shared/services/list.service';
import { IgnoredTimesheetsComponent } from '../../ignored-timesheets/ignored-timesheets/ignored-timesheets.component';
import { UserService } from '../../../../services/user.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { AccordionComponent } from '../../../../shared/components/accordion/accordion.component';

@Component({
  selector: 'ppl-claim-ledger-list',
  standalone: true,
  imports: [AccordionComponent, ListTableComponent, FormsModule, NzInputModule],
  templateUrl: './ledger-list.component.html',
  styleUrl: './ledger-list.component.css',
})
export class LedgerListComponent implements OnInit {
  sharedDataKey: string = STATIC_DATA.SHARED_BATCHES_DATA;
  pageId: string = PAGE_CONSTANTS.BATCHES_GRID_PAGE;
  programName!: string;
  lazyLoad = true;

  // Claim ID passed in from the Consumer Ledger page (via navigation state)
  selectedClaimId: string | number | null = null;

  // Claim Family ID passed in from the Consumer Ledger page (via navigation state) -
  // used to look up the Net Ledger, which spans every claim in the family (original,
  // adjustments, voids, ...).
  selectedClaimFamilyId: string | number | null = null;

  // Claim summary view - populated from the API once the selected claim loads
  claim: any = null;

  // CDR Response view - populated from the API once the selected claim loads
  cdr: any = null;

  // Net Ledger table configuration
  netLedgerCols = [
    { field: 'category', header: 'Category' },
    { field: 'event', header: 'Event' },
    { field: 'claim', header: 'Claim' },
    { field: 'eventDate', header: 'Event Date' },
    { field: 'debit', header: 'Debit' },
    { field: 'credit', header: 'Credit' },
  ];

  // Net Ledger rows/totals - populated from the API once the selected claim loads
  netLedgerData: any[] = [];
  netLedgerTotals = this.computeNetLedgerTotals([]);

  // Source Timesheets table configuration
  sourceTimesheetCols = [
    { field: 'timesheetID', header: 'Timesheet ID' },
    { field: 'providerFullname', header: 'Provider' },
    { field: 'serviceDate', header: 'Created Date' },
    { field: 'serviceDuration', header: 'Time In / Out' },
    { field: 'serviceHours', header: 'Hours' },
    { field: 'serviceUnits', header: 'Units' },
    { field: 'charges', header: 'Charges' },
    { field: 'submittedDate', header: 'Service Start Date' },
    { field: 'approvedDate', header: 'Service End Date' },
    { field: 'status', header: 'Status' },
  ];

  // Maps of processing-status codes to display labels for the Status column
  private readonly timesheetStatusLabels: Record<string, string> = {
    P: 'Pending',
    RP: 'Ready to Process',
    RV: 'In Review',
  };

  // Populated from /billingapi/v1/getListPageData for the selected claim
  sourceTimesheetData: any[] = [];

  // Payments table configuration
  paymentCols = [
    { field: 'paymentDate', header: 'Payment Date' },
    { field: 'checkNumber', header: 'Check Number' },
    { field: 'patientPaidAmount', header: 'Patient Paid Amount' },
    { field: 'totalPaidAmount', header: 'Total Payment Amount' },
    { field: 'carcCode', header: 'CARC' },
    { field: 'rarcCode', header: 'RARC' },
    { field: 'paymentType', header: 'Payment Type' },
    { field: 'remitType', header: 'Remit Type' },
    { field: 'depositMatched', header: 'Deposit Received' },
  ];

  // Populated from /billingapi/v1/getListPageData for the selected claim
  paymentData: any[] = [];

  // PPL ICN search box (header) - looks up another claim and loads its ledger
  searchPplIcn = '';
  searchPplIcnError = '';

  constructor(
    private router: Router,
    private sharedDataService: SharedDataService,
    private sectionService: SectionsService,
    private claimsService: ClaimsService,
    private billingService: BillingService,
    private listService: ListService,
    private userService: UserService,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';

    const navigationState = window.history.state;
    if (navigationState?.claimId != null) {
      this.selectedClaimId = navigationState.claimId;
    }
    if (navigationState?.claimFamilyId != null) {
      this.selectedClaimFamilyId = navigationState.claimFamilyId;
    }

    this.loadClaimLedger();
  }

  // Fetches every section for the currently selected claim/claim family.
  private loadClaimLedger(): void {
    this.loadClaimSummary();
    this.loadCdrResponse();
    this.loadPayments();
    this.loadSourceTimesheets();
    this.loadNetLedger();
  }

  // Search icon clicked -> resolve the entered PPL ICN to a claim
  // (billingapi/v1/claimGlobalSearch/{programCode}?pplIcn=...) and reload every section
  // for that claim.
  onSearchByPplIcn(): void {
    const pplIcn = this.searchPplIcn.trim();
    if (!pplIcn) {
      return;
    }
    this.searchPplIcnError = '';
    this.billingService.claimGlobalSearch(this.programName || 'NY', pplIcn).subscribe({
      next: (response: any) => {
        const result = response?.responsedata;
        if (result?.tasBillingBatchClaimDetailId == null) {
          this.logger.error('No claim found for PPL ICN', pplIcn);
          this.searchPplIcnError = `No claim found for ICN ${pplIcn}`;
          return;
        }
        this.selectedClaimId = result.tasBillingBatchClaimDetailId;
        this.selectedClaimFamilyId = result.claimFamilyId ?? null;
        this.searchPplIcn = '';
        this.loadClaimLedger();
      },
      error: (err) => {
        this.logger.error('Failed to search claim by PPL ICN', err);
        this.searchPplIcnError = this.backendErrorMessage(err) ?? `No claim found for ICN ${pplIcn}`;
      },
    });
  }

  // Builds the common /billingapi/v1/getListPageData request body for the selected claim.
  // All Claim Ledger sections share this shape - only pageName differs.
  private buildClaimSectionPayload(pageName: string): any {
    return {
      pageName,
      programCode: this.programName || 'NY',
      condition: 'and',
      recordsPerPage: 10,
      startingPageNumber: 1,
      orderByColumn: [],
      orderBy: [],
      searchParam: [
        {
          jsonPath: 'tasBillingBatchClaimDetailId',
          operator: 'equal',
          value: `${this.selectedClaimId}`,
        },
      ],
    };
  }

  // Fetches the Claim summary card for the selected claim (pageName: billingClaimSummaryView).
  private loadClaimSummary(): void {
    if (this.selectedClaimId == null) {
      return;
    }
    const payload = this.buildClaimSectionPayload('billingClaimSummaryView');
    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const row = response?.responsedata?.[0];
        if (row) {
          this.claim = this.buildClaimView(row);
          // CDR's External ICN is sourced from the claim - refresh it now that the claim has loaded
          // (only if CDR has already loaded; if not, buildCdrView will pick up claim.extIcn directly).
          if (this.cdr) {
            this.cdr = { ...this.cdr, externalIcn: this.claim.extIcn };
          }
        }
      },
      error: (err) => {
        this.logger.error('Failed to load claim summary', err);
      },
    });
  }

  // Fetches the CDR Response section for the selected claim (pageName: billingCdrFileDetailView).
  private loadCdrResponse(): void {
    if (this.selectedClaimId == null) {
      return;
    }
    const payload = this.buildClaimSectionPayload('billingCdrFileDetailView');
    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const rows: any[] = response?.responsedata ?? [];
        if (rows.length) {
          this.cdr = this.buildCdrView(rows);
        }
      },
      error: (err) => {
        this.logger.error('Failed to load CDR response', err);
      },
    });
  }

  // Fetches the Payments grid for the selected claim (pageName: claimPaymentsView).
  // Unlike the other claim sections (which search by tasBillingBatchClaimDetailId),
  // this searches by claimFamilyId - same convention as loadNetLedger() below.
  private loadPayments(): void {
    if (this.selectedClaimFamilyId == null) {
      this.paymentData = [];
      return;
    }
    const payload = {
      pageName: 'claimPaymentsView',
      programCode: this.programName || 'NY',
      condition: 'and',
      recordsPerPage: 10,
      startingPageNumber: 1,
      orderByColumn: [],
      orderBy: [],
      searchParam: [
        {
          jsonPath: 'claimFamilyId',
          operator: 'equal',
          value: `${this.selectedClaimFamilyId}`,
        },
      ],
    };
    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const rows: any[] = response?.responsedata ?? [];
        this.paymentData = rows.map((p) => this.mapPaymentRow(p));
      },
      error: (err) => {
        this.logger.error('Failed to load payments', err);
      },
    });
  }

  // Fetches the Net Ledger grid for the selected claim (pageName: claimNetLedgerView).
  // Unlike the other sections, this searches by claimFamilyId and pages up to 200 rows
  // at once (a claim's full ledger history is expected to be small).
  private loadNetLedger(): void {
    if (this.selectedClaimFamilyId == null) {
      this.netLedgerData = [];
      this.netLedgerTotals = this.computeNetLedgerTotals([]);
      return;
    }

    const payload: any = {
      pageName: 'claimNetLedgerView',
      programCode: this.programName || 'NY',
      condition: 'and',
      recordsPerPage: 200,
      startingPageNumber: 1,
      orderByColumn: [],
      orderBy: [],
      searchParam: [
        {
          jsonPath: 'claimFamilyId',
          operator: 'equal',
          value: this.selectedClaimFamilyId,
        },
      ],
    };

    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const rows: any[] = response?.responsedata ?? [];
        this.netLedgerData = rows.map((e) => this.mapNetLedgerRow(e));
        this.netLedgerTotals = this.computeNetLedgerTotals(rows);
      },
      error: (err) => {
        this.logger.error('Failed to load net ledger', err);
      },
    });
  }

  // Fetches the Source Timesheets grid for the selected claim from
  // /billingapi/v1/getListPageData (ListService, from = 'billing').
  private loadSourceTimesheets(): void {
    if (this.selectedClaimId == null) {
      this.sourceTimesheetData = [];
      return;
    }

    const payload = this.buildClaimSectionPayload('billingClaimTimesheetView');

    this.listService.getListData(payload, 'billing').subscribe({
      next: (response: any) => {
        if (response?.responsemsg?.[0]?.code === 'INTERNAL_SERVER_ERROR') {
          return;
        }
        const rows: any[] = response?.responsedata ?? [];
        this.sourceTimesheetData = rows.map((ts) => this.mapTimesheetRow(ts));
      },
      error: (err) => {
        this.logger.error('Failed to load source timesheets', err);
      },
    });
  }

  // Maps a raw /claimNetLedgerView row to the Net Ledger grid columns.
  // The "root" claim for the family is tasBillingBatchClaimDetailId (constant across all
  // rows in the response); a row is the "current" claim when its subjectClaimId matches it.
  private mapNetLedgerRow(e: any) {
    const claimPplIcn = e?.subjectPplIcn ?? e?.pplIcn;
    const isCurrentClaim = e?.subjectClaimId === e?.tasBillingBatchClaimDetailId;
    const debit = e?.debitAmount;
    const credit = e?.creditAmount;

    const entryType = (e?.entryType ?? '').toUpperCase();

    return {
      category: this.toTitleCase(e?.entryType),
      // "Event" column: a static title per entry type, plus a dynamic note built from
      // subjectPplIcn/relatedPplIcn (see netLedgerEventNote).
      eventTitle: this.netLedgerEventTitle(entryType),
      eventNote: this.netLedgerEventNote(entryType, e?.subjectPplIcn, e?.relatedPplIcn),
      // "Claim" column: current claim renders as plain text; other claims in the family
      // are clickable so the user can jump to that claim's own ledger page.
      claimIcn: claimPplIcn,
      claimId: e?.subjectClaimId,
      isCurrentClaim,
      eventDate: this.formatDate(e?.eventDate),
      debit: debit != null ? this.formatCurrency(debit) : '—',
      credit: credit != null ? this.formatCurrency(credit) : '—',
      // Only color non-zero amounts - a literal $0 shouldn't read as a debit/credit signal.
      debitPositive: debit != null && Number(debit) > 0,
      creditPositive: credit != null && Number(credit) > 0,
    };
  }

  // Non-current claim ICN clicked in the Net Ledger's Claim column -> open that claim's
  // own Claim Ledger page. We already have its claimId (subjectClaimId) from the net ledger
  // response, so this reuses the same navigation the Consumer Ledger grid uses.
  onNetLedgerClaimClick(payload: any): void {
    const claimId = payload?.event?.claimId;
    if (claimId == null) {
      return;
    }
    this.router.navigate(['/home/claimLedgerList'], {
      state: { claimId },
    });
  }

  // Static Event title per entry type
  private netLedgerEventTitle(entryType: string): string {
    const titles: Record<string, string> = {
      ORIGINAL: 'Claim Billed',
      PAYMENT: 'Payment Received',
      VOIDANCE: 'Void Claim Generated',
      ADJUSTMENT: 'Replacement Claim Billed',
      REVERSAL: 'Payment Reversed',
    };
    return titles[entryType] ?? this.toTitleCase(entryType);
  }

  // Dynamic note under the Event title, built from the subject/related claim ICNs
  private netLedgerEventNote(entryType: string, subjectPplIcn: string, relatedPplIcn: string): string {
    switch (entryType) {
      case 'ADJUSTMENT':
        return `Claim ${subjectPplIcn} replaces ${relatedPplIcn}`;
      case 'VOIDANCE':
        return `Claim ${subjectPplIcn} voids ${relatedPplIcn}`;
      case 'ORIGINAL':
      case 'PAYMENT':
      default:
        return `Claim ${subjectPplIcn}`;
    }
  }

  // Sums the raw debit/credit amounts across net ledger rows (the list endpoint doesn't
  // return a totals object, so these are computed client-side).
  private computeNetLedgerTotals(rows: any[]) {
    const totalDebit = rows.reduce((sum, e) => sum + Number(e?.debitAmount ?? 0), 0);
    const totalCredit = rows.reduce((sum, e) => sum + Number(e?.creditAmount ?? 0), 0);

    return {
      totalDebit: this.formatCurrency(totalDebit),
      totalCredit: this.formatCurrency(totalCredit),
      netTotal: this.formatCurrency(totalCredit - totalDebit),
    };
  }

  // Maps a raw /claimPaymentsView payment row to the Payments grid columns
  // (falls back to the old dummy-data field names)
  private mapPaymentRow(p: any) {
    return {
      // API already returns paymentDate as MM/DD/YYYY - display as-is
      paymentDate: p?.paymentDate ?? '-',
      checkNumber: p?.paymentNumber ?? p?.checkNumber ?? '-',
      patientPaidAmount:
        p?.patientPaidAmount != null ? this.formatCurrency(p.patientPaidAmount) : '-',
      totalPaidAmount:
        (p?.paymentAmount ?? p?.totalPaidAmount) != null
          ? this.formatCurrency(p.paymentAmount ?? p.totalPaidAmount)
          : '-',
      carcCode: p?.primaryCarc || p?.carcCode || '-',
      rarcCode: p?.primaryRarc || p?.rarcCode || '-',
      paymentType: p?.paymentType || '-',
      remitType: p?.remitType || '-',
      // Only an explicit boolean true counts as received - anything else (false, null, missing) is NO.
      depositMatched: p?.isDepositReceived === true ? 'YES' : 'NO',
    };
  }

  // Maps a raw /getListPageData timesheet row to the Source Timesheets grid columns
  private mapTimesheetRow(ts: any) {
    return {
      timesheetID: ts?.tasTimesheetlineDetailId ?? '-',
      providerFullname: ts?.providerName ?? '-',
      serviceDate: ts?.createddate ? this.formatDate(ts.createddate) : '-',
      serviceDuration: this.formatTimeRange(ts?.serviceStart, ts?.serviceEnd),
      serviceHours: ts?.serviceDurationHours ?? '-',
      serviceUnits: ts?.billedUnits ?? '-',
      charges:
        ts?.tsCharges != null && !isNaN(Number(ts.tsCharges))
          ? this.formatCurrency(ts.tsCharges)
          : '-',
      submittedDate: ts?.serviceStart
        ? this.formatDate(this.datePart(ts.serviceStart))
        : '-',
      approvedDate: ts?.serviceEnd
        ? this.formatDate(this.datePart(ts.serviceEnd))
        : '-',
      status:
        this.timesheetStatusLabels[ts?.timesheetStatus] ??
        ts?.timesheetStatus ??
        '-',
    };
  }

  // "2025-05-11 11:37:00" -> "2025-05-11"
  private datePart(dateTime: string): string {
    return (dateTime ?? '').split(' ')[0];
  }

  // ("2025-05-11 11:37:00", "2025-05-11 11:38:00") -> "11:37 - 11:38"
  private formatTimeRange(start: string, end: string): string {
    const toTime = (dt: string) => ((dt ?? '').split(' ')[1] ?? '').slice(0, 5);
    const s = toTime(start);
    const e = toTime(end);
    if (!s && !e) {
      return '-';
    }
    return `${s || '—'} - ${e || '—'}`;
  }

  // Builds display-ready claim summary values from the raw /billingClaimSummaryView API shape
  // (falls back to the old claimXxx field names so the hardcoded fallback data still works)
  private buildClaimView(c: any) {
    const frequencyLabels: Record<string, string> = {
      '1': 'Original',
      '7': 'Replacement',
      '8': 'Void',
    };
    const paymentStatusLabels: Record<string, string> = {
      PD: 'Paid',
      PP: 'Partially Paid',
      DN: 'Denied',
      OP: 'Open',
    };
    const frequencyCode = c.frequencyCode ?? c.claimFrequencyCode;
    const frequencyName = frequencyLabels[`${frequencyCode}`] ?? '';
    const paymentStatus = c.status ?? c.claimPaymentStatus;
    const diagnosisCodes = c.diagnosisCode ?? c.claimDiagnosisCodes;

    return {
      pplIcn: c.pplIcn ?? c.claimPplIcn,
      extIcn: c.extIcn ?? c.claimExtIcn,
      claimType: c.claimType ?? (c.fileType ? c.fileType.toUpperCase() : '-'),
      frequency: frequencyName ? `${frequencyCode} · ${frequencyName}` : `${frequencyCode}`,
      frequencyBadge: frequencyName
        ? `FREQ ${frequencyCode} · ${frequencyName.toUpperCase()}`
        : `FREQ ${frequencyCode}`,
      batchName: c.claimBatchName ?? c.fileName,
      mcoName: c.mcoName ?? c.claimMcoName,
      serviceDate: this.formatDate(c.serviceDate ?? c.claimServiceDate),
      serviceCode: c.serviceModifier ?? c.claimServiceCode,
      authNumber: c.authNumber ?? c.claimAuthNumber,
      diagnosisCodes: Array.isArray(diagnosisCodes)
        ? diagnosisCodes.join(', ')
        : diagnosisCodes || '-',
      participantPplId: c.participantPplId ?? c.claimParticipantPplId,
      county: c.participantCounty ?? c.claimParticipantCounty,
      mcoParticipantId: c.mcoParticipantId ?? c.claimMcoParticipantId,
      medicaidParticipantId: c.medicaidParticipantID ?? c.medicaidParticipantId ?? c.claimMedicaidParticipantId ?? '-',
      planCode: c.planCode ?? c.claimPlanCode ?? '-',
      units: c.units ?? c.claimUnits,
      billRate: this.formatCurrency(c.billableRate ?? c.claimBillRate),
      paymentStatus: paymentStatusLabels[paymentStatus] ?? paymentStatus,
      creationDate: this.formatDate(c.createddate ?? c.claimCreationDate),
      billedAmount: this.formatCurrency(c.totalCharges ?? c.claimBilledAmount),
      paymentAmount: this.formatCurrency(c.paymentAmount ?? c.claimPaymentAmount),
      openAR: this.formatCurrency(c.openar ?? c.claimOpenAR),
      // $0 AR is a good outcome (nothing owed) - keep it non-threatening rather than red.
      openARIsZero: Number(c.openar ?? c.claimOpenAR ?? 0) === 0,
    };
  }

  // Builds the display-ready CDR Response view from an array of /billingCdrFileDetailView
  // events for the claim (most recent event drives the summary row).
  private buildCdrView(rows: any[]): any {
    const list = Array.isArray(rows) ? rows : [rows];
    const latest = list[list.length - 1] ?? {};

    const events = list.map((e) => this.buildCdrEvent(e));

    return {
      fileName: latest.cdrFileName,
      received: this.formatDateTime(latest.eventDatetime),
      externalIcn: this.claim?.extIcn,
      ...this.buildCdrEvent(latest),
      events,
    };
  }

  private buildCdrEvent(e: any) {
    const eventCodeLabels: Record<string, string> = {
      'A1': 'Accepted',
      'A2': 'Accepted with Errors',
      'R1': 'Rejected',
      'E1': 'Error',
      'A-P': 'Acknowledged',
      'FP-T': 'Front-End Pass Through',
    };
    const eventName = eventCodeLabels[e?.eventCode];

    return {
      eventCode: e?.eventCode,
      eventCodeLabel: eventName ? `${eventName} (${e.eventCode})` : e?.eventCode,
      isAccepted: typeof e?.eventCode === 'string' && e.eventCode.startsWith('A'),
      eventMessage: e?.delimitedSimpleResp || e?.eventMessage || '—',
      eventDate: this.formatDate(e?.eventDate),
    };
  }

  // "2025-05-19 11:02:55" -> "05/19/2025 11:02"
  private formatDateTime(dateTime: string): string {
    if (!dateTime) {
      return '-';
    }
    const [datePart, timePart] = dateTime.split(' ');
    const dateFormatted = this.formatDate(datePart);
    const time = (timePart ?? '').slice(0, 5);
    return time ? `${dateFormatted} ${time}` : dateFormatted;
  }

  // "2025-03-02" -> "03/02/2025"
  private formatDate(iso: string): string {
    if (!iso) {
      return '-';
    }
    const [year, month, day] = iso.split('-');
    return `${month}/${day}/${year}`;
  }

  // 180.09 -> "$180.09", -180.09 -> "-$180.09", null/undefined/non-numeric -> "-"
  private formatCurrency(value: number | null | undefined): string {
    const num = Number(value);
    if (value == null || isNaN(num)) {
      return '-';
    }
    const sign = num < 0 ? '-' : '';
    return `${sign}$${Math.abs(num).toFixed(2)}`;
  }

  // "VOID_CLAIM_GENERATED" -> "Void Claim Generated"
  private toTitleCase(value: string): string {
    if (!value) {
      return '';
    }
    return value
      .toLowerCase()
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Pulls the backend's specific error message (e.g. "No claim found for the given
  // pplIcn") out of an HttpErrorResponse's responsemsg[] payload, if present.
  private backendErrorMessage(err: any): string | null {
    const desc = err?.error?.responsemsg?.[0]?.desc;
    return typeof desc === 'string' && desc.trim() ? desc : null;
  }

}
